import { UseMutationResult } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Divider } from 'primereact/divider';
import { Skeleton } from 'primereact/skeleton';
import { PcAnalyticsResponse } from '~/api/def/endpoints/portfolioCompanyEndpoint';
import { ShareIssuanceTable } from '~/components/ui/tables/ag/shareIssuance/shareIssuanceTable';
import { formatCurrency } from '~/lib/money';
import CurrentValuationBarChart from '~/lib/recharts/currentValuationBarChart';
import GrossMoicBarChart from '~/lib/recharts/grossMoicBarChart';
import PCompanyInvestorPieChart from '~/lib/recharts/pCompanyInvestorPieChart';

type PerformanceSectionProps = {
  portfolioAnalytics: PcAnalyticsResponse;
  isLoading: boolean;
  error: Error | null;
  recalculateValuationMutation?: UseMutationResult<void, Error, void, unknown>;
};

export default function PerformanceSection({
  portfolioAnalytics,
  isLoading,
  error,
  recalculateValuationMutation,
}: PerformanceSectionProps) {
  const moicData = [
    { name: 'Gross MOIC', value: portfolioAnalytics.grossMoic },
  ];

  return (
    <div>
      <div className="grid grid-nogutter mt-3">
        <div
          className="col-12 flex text-sm border-noround-bottom gray-border"
          style={{
            borderRadius: '8px',
          }}
        >
          <div className="col-3 p-3">
            <p style={{ color: '#65748b' }} className="mb-1 mt-0 font-medium">
              Incorporation Date
            </p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-semibold">
                {portfolioAnalytics.incorporationDate
                  ? format(
                      new Date(portfolioAnalytics.incorporationDate),
                      'yyyy-MM-dd',
                    )
                  : '-'}
              </h4>
            )}
          </div>
          <Divider className="m-0" layout="vertical" />

          <div className="col-3 p-3">
            <p style={{ color: '#65748b' }} className="mb-1 mt-0 font-medium">
              Current IRR
            </p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-semibold">
                {portfolioAnalytics.currentIrr
                  ? parseFloat(
                      portfolioAnalytics.currentIrr.toString(),
                    ).toFixed(2)
                  : 0}
              </h4>
            )}
          </div>
          <Divider className="m-0" layout="vertical" />

          <div className="col-3 p-3">
            <p style={{ color: '#65748b' }} className="mb-1 mt-0 font-medium">
              ESOP
            </p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-semibold">
                {portfolioAnalytics.currentEsop
                  ? parseFloat(
                      portfolioAnalytics.currentEsop.toString(),
                    ).toFixed(2)
                  : 0}
              </h4>
            )}
          </div>
          <Divider className="m-0" layout="vertical" />

          <div className="col-3 p-3">
            <p style={{ color: '#65748b' }} className="mb-1 mt-0 font-medium">
              TVPI
            </p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-semibold">
                {portfolioAnalytics.tvpi
                  ? parseFloat(portfolioAnalytics.tvpi.toString()).toFixed(2)
                  : 0}
              </h4>
            )}
          </div>
        </div>

        <div className="col-12 flex text-sm border-top-none gray-border">
          <div className="col-3 p-3">
            <p style={{ color: '#65748b' }} className="mb-1 mt-0 font-medium">
              Unrealized
            </p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-semibold">
                {portfolioAnalytics.unrealised
                  ? formatCurrency(
                      portfolioAnalytics.currency ?? 'EUR',
                      portfolioAnalytics.unrealised,
                    )
                  : 0}
              </h4>
            )}
          </div>
          <Divider className="m-0" layout="vertical" />

          <div className="col-3 p-3">
            <p style={{ color: '#65748b' }} className="mb-1 mt-0 font-medium">
              Realized
            </p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-semibold">
                {portfolioAnalytics.realised
                  ? formatCurrency(
                      portfolioAnalytics.currency ?? 'EUR',
                      portfolioAnalytics.realised,
                    )
                  : 0}
              </h4>
            )}
          </div>
          <Divider className="m-0" layout="vertical" />

          <div className="col-3 p-3">
            <p style={{ color: '#65748b' }} className="mb-1 mt-0 font-medium">
              Net MOIC
            </p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-semibold">
                {portfolioAnalytics.netMoic
                  ? portfolioAnalytics.netMoic.toFixed(2) + '%'
                  : 0 + '%'}
              </h4>
            )}
          </div>
          <Divider className="m-0" layout="vertical" />

          <div className="col-3 p-3">
            <p style={{ color: '#65748b' }} className="mb-1 mt-0 font-medium">
              Ownership
            </p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-semibold">
                {portfolioAnalytics.ownership
                  ? portfolioAnalytics.ownership.toFixed(2) + '%'
                  : 0 + '%'}
              </h4>
            )}
          </div>
        </div>

        <div className="col-12 flex text-sm border-top-none gray-border">
          <div className="col-3 p-3">
            <p style={{ color: '#65748b' }} className="mb-1 mt-0 font-medium">
              Current Price
            </p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-semibold">
                {portfolioAnalytics.currentPrice
                  ? formatCurrency(
                      portfolioAnalytics.currency ?? 'EUR',
                      portfolioAnalytics.currentPrice,
                    )
                  : 0}
              </h4>
            )}
          </div>
          <Divider className="m-0" layout="vertical" />

          <div className="col-3 p-3">
            <p style={{ color: '#65748b' }} className="mb-1 mt-0 font-medium">
              Current Fully Diluted
            </p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-semibold">
                {portfolioAnalytics.currentFullyDiluted}
              </h4>
            )}
          </div>
          <Divider className="m-0" layout="vertical" />

          <div className="col-3 p-3">
            <p style={{ color: '#65748b' }} className="mb-1 mt-0 font-medium">
              No of Invertors
            </p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-semibold">
                {portfolioAnalytics.noOfInvestors}
              </h4>
            )}
          </div>
          <Divider className="m-0" layout="vertical" />

          <div className="col-3 p-3">
            <p style={{ color: '#65748b' }} className="mb-1 mt-0 font-medium">
              Fund Shares
            </p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-semibold">
                {portfolioAnalytics.totalFundShares
                  ? portfolioAnalytics.totalFundShares.toFixed(2)
                  : 0}
              </h4>
            )}
          </div>
        </div>
      </div>

      <div className="flex mt-3 gap-3 w-full">
        <div
          className="w-full h-25rem gray-border"
          style={{
            borderRadius: '8px',
          }}
        >
          <CurrentValuationBarChart
            data={portfolioAnalytics.valuationOverTime}
            currency={portfolioAnalytics.currency}
            isLoading={isLoading}
            error={error}
          />
        </div>
      </div>

      <div className="flex mt-3 gap-3 w-full">
        <div
          className="w-6 h-25rem gray-border"
          style={{
            borderRadius: '8px',
          }}
        >
          <PCompanyInvestorPieChart
            data={portfolioAnalytics.investors}
            isLoading={isLoading}
            error={error}
          />
        </div>
        <div
          className="w-6 h-25rem gray-border"
          style={{
            borderRadius: '8px',
          }}
        >
          <GrossMoicBarChart
            data={moicData}
            isLoading={isLoading}
            error={error}
          />
        </div>
      </div>

      <div
        className="flex flex-column mt-3 w-full p-3 h-24rem gray-border"
        style={{
          borderRadius: '8px',
        }}
      >
        <ShareIssuanceTable
          pCompanyId={portfolioAnalytics.id}
          recalculateValuationMutation={recalculateValuationMutation}
        />
      </div>
    </div>
  );
}
